import { HttpRequestError } from '../shared/request-error.js';
import { isSatelliteBasemap } from '../shared/satellite-access.js';
import { getBasemapDefinition, azureHybridLabelsUrl } from './map-shared.js';
import type { Map as GlobeMap, RasterSourceSpecification, StyleSpecification } from 'maplibre-gl';

type GlobeBasemapStyle = StyleSpecification & { sprite?: string };
export const GLOBE_VECTOR_ATTRIBUTION = '<a href="https://openfreemap.org/">OpenFreeMap</a> © <a href="https://openmaptiles.org/">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

export function createGlobeImageryOptions(basemapId: string): RasterSourceSpecification {
  const globe = getBasemapDefinition(basemapId).globe;
  const subdomains = 'subdomains' in globe ? globe.subdomains : [''];
  return { type: 'raster', tiles: subdomains.map(subdomain => globe.url.replace('{s}', subdomain)), minzoom: isSatelliteBasemap(basemapId) ? 1 : 0, maxzoom: globe.maximumLevel, tileSize: 256, attribution: globe.credit };
}

export function isGlobeBasemapLayer(id: string): boolean {
  return id === 'basemap' || id.startsWith('basemap:');
}

export async function loadGlobeBasemap(basemapId: string): Promise<GlobeBasemapStyle> {
  if (basemapId === 'hybrid') {
    const imagery = createGlobeImageryOptions('hybrid');
    return { version: 8, projection: { type: 'globe' }, sources: { basemap: imagery, 'basemap:labels': { ...imagery, tiles: [azureHybridLabelsUrl()] } }, layers: [
      { id: 'basemap', type: 'raster', source: 'basemap', paint: { 'raster-fade-duration': 0 } },
      { id: 'basemap:labels', type: 'raster', source: 'basemap:labels', paint: { 'raster-fade-duration': 0 } },
    ] };
  }
  const definition = getBasemapDefinition(basemapId);
  if (!('styleUrl' in definition)) {
    return { version: 8, projection: { type: 'globe' }, sources: { basemap: createGlobeImageryOptions(basemapId) }, layers: [{ id: 'basemap', type: 'raster', source: 'basemap', paint: { 'raster-fade-duration': 0 } }] };
  }
  const response = await fetch(definition.styleUrl);
  if (!response.ok) throw new HttpRequestError(`Could not load the globe basemap (${response.status})`, response, 'globe-basemap');
  const style: GlobeBasemapStyle = await response.json();
  return {
    ...style,
    projection: { type: 'globe' },
    sources: Object.fromEntries(Object.entries(style.sources).map(([id, source]) => [`basemap:${id}`, { ...source, attribution: GLOBE_VECTOR_ATTRIBUTION }])),
    layers: style.layers.map(layer => {
      const renamed = { ...layer, id: `basemap:${layer.id}` };
      if ('source' in renamed) renamed.source = `basemap:${renamed.source}`;
      if (renamed.type === 'symbol') renamed.layout = { ...renamed.layout, 'text-pitch-alignment': 'viewport', 'icon-pitch-alignment': 'viewport' };
      return renamed;
    }),
  };
}

export function replaceGlobeBasemap(map: GlobeMap, style: GlobeBasemapStyle): void {
  const previous = map.getStyle();
  for (const layer of previous.layers) if (isGlobeBasemapLayer(layer.id)) map.removeLayer(layer.id);
  for (const id of Object.keys(previous.sources)) if (isGlobeBasemapLayer(id)) map.removeSource(id);
  if (style.glyphs && style.glyphs !== previous.glyphs) map.setGlyphs(style.glyphs);
  if (style.sprite && style.sprite !== previous.sprite) map.setSprite(style.sprite);
  for (const [id, source] of Object.entries(style.sources)) map.addSource(id, source);
  const layers = map.getStyle().layers;
  const ground = layers[0]?.id;
  const labels = layers.find(layer => layer.type === 'circle' || layer.type === 'symbol')?.id;
  for (const layer of style.layers) map.addLayer(layer, layer.type === 'symbol' ? labels : ground);
}
